import { useEffect, useMemo } from 'react'
import { Popover, PopoverAnchor, PopoverContent } from './ui/popover'
import { cn } from '../lib/utils'

export type OneShotTipDismissReason = 'timeout' | 'outside' | 'tap'

interface Props {
  open: boolean
  onDismiss: (via: OneShotTipDismissReason) => void
  body: React.ReactNode
  anchor: HTMLElement | null
  side?: 'top' | 'bottom' | 'left' | 'right'
  sideOffset?: number
  align?: 'start' | 'center' | 'end'
  className?: string
}

const AUTO_DISMISS_MS = 10000

export function OneShotTip({
  open,
  onDismiss,
  body,
  anchor,
  side = 'top',
  sideOffset = 6,
  align = 'center',
  className,
}: Props) {
  // Radix only reads `.current` off this; a plain object avoids a render-time
  // ref write while still repositioning the moment the anchor changes.
  const virtualRef = useMemo(() => ({ current: anchor }), [anchor])

  useEffect(() => {
    if (!open) return
    const id = window.setTimeout(() => onDismiss('timeout'), AUTO_DISMISS_MS)
    return () => window.clearTimeout(id)
  }, [open, onDismiss])

  const effectiveOpen = open && !!anchor

  return (
    <Popover
      open={effectiveOpen}
      onOpenChange={next => {
        if (!next) onDismiss('outside')
      }}
    >
      <PopoverAnchor virtualRef={virtualRef as React.RefObject<HTMLElement>} />
      <PopoverContent
        side={side}
        sideOffset={sideOffset}
        align={align}
        arrow
        // Keeps a tip anchored near the screen edge from sitting flush against it.
        collisionPadding={12}
        onOpenAutoFocus={e => e.preventDefault()}
        onClick={() => onDismiss('tap')}
        className={cn(
          // Tips float over busy, dark card grids: a brighter outline and a
          // heavy drop shadow lift them off it without changing their color.
          'w-auto max-w-[260px] py-2 px-3 text-xs leading-snug font-medium cursor-pointer shadow-lg shadow-black/70',
          '[--floater-edge:color-mix(in_oklab,var(--color-foreground)_30%,transparent)]',
          className,
        )}
      >
        {body}
      </PopoverContent>
    </Popover>
  )
}
